import React from 'react';
import { Heart, MessageCircle, Users, Star, Image as ImageIcon, Bell, BellOff } from 'lucide-react';
import { CandidateWithSlides } from '../../lib/supabase';
import { getUniversity } from '../../lib/constants';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';

interface ResearcherCardProps {
  candidate: CandidateWithSlides;
  likeCount: number;
  commentCount: number;
  participateCount: number;
  avgRating: number;
  ratingCount: number;
  isMuted?: boolean;
  onToggleMute?: () => void;
  onClick: () => void;
}

export default function ResearcherCard({
  candidate,
  likeCount,
  commentCount,
  participateCount,
  avgRating,
  ratingCount,
  isMuted = false,
  onToggleMute,
  onClick,
}: ResearcherCardProps) {
  const thumbnailUrl =
    candidate.slide_images?.find((s) => s.slide_index === candidate.key_slide_index)?.image_url ||
    candidate.slide_images?.[0]?.image_url;

  return (
    <Card
      className="hover:shadow-md transition-all duration-200 cursor-pointer group relative"
      onClick={onClick}
    >
      {/* Mute toggle */}
      {onToggleMute && (
        <button
          type="button"
          className="absolute top-2 right-2 z-10 p-1 rounded-full hover:bg-muted/80 transition-colors"
          onClick={(e) => { e.stopPropagation(); onToggleMute(); }}
          title={isMuted ? '通知をオンにする' : '通知をミュート'}
          aria-label={isMuted ? `${candidate.name}さんの通知をオンにする` : `${candidate.name}さんの通知をミュート`}
          aria-pressed={isMuted}
        >
          {isMuted ? (
            <BellOff className="h-4 w-4 text-muted-foreground/50" />
          ) : (
            <Bell className="h-4 w-4 text-muted-foreground/30 group-hover:text-muted-foreground/60" />
          )}
        </button>
      )}

      <CardContent className="p-3">
        {/* Thumbnail */}
        <div className="w-full aspect-[4/3] bg-muted rounded border overflow-hidden mb-3 flex items-center justify-center">
          {thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt={candidate.research_topic}
              className="w-full h-full object-cover"
            />
          ) : (
            <ImageIcon className="h-8 w-8 text-muted-foreground" />
          )}
        </div>

        {/* Info */}
        <div className="flex items-center gap-2 mb-1">
          {candidate.avatar_url ? (
            <img src={candidate.avatar_url} alt={`${candidate.name}のプロフィール画像`} className="w-6 h-6 rounded-full object-cover flex-shrink-0" />
          ) : (
            <div className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-[9px] font-bold text-white ${getUniversity(candidate.university).color}`}>
              {candidate.name.charAt(0)}
            </div>
          )}
          <span className="text-xs text-muted-foreground truncate">{candidate.name}</span>
          <Badge variant="secondary" className="text-[9px] px-1.5 py-0">研究者</Badge>
        </div>
        <h4 className="text-sm font-semibold text-foreground line-clamp-2 group-hover:text-primary transition-colors mb-1">
          {candidate.research_topic}
        </h4>
        <p className="text-xs text-muted-foreground truncate">
          {candidate.affiliation}
          {candidate.department && ` / ${candidate.department}`}
        </p>

        {/* Research Overview */}
        {candidate.research_overview && (
          <p className="text-xs text-muted-foreground mt-2 line-clamp-3 leading-relaxed">
            {candidate.research_overview}
          </p>
        )}

        {/* Metrics */}
        <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
          {ratingCount > 0 && (
            <span className="flex items-center">
              <Star className="h-3 w-3 mr-0.5 text-yellow-500" />
              {avgRating.toFixed(1)}
            </span>
          )}
          <span className="flex items-center">
            <Heart className="h-3 w-3 mr-0.5 text-red-500" />
            {likeCount}
          </span>
          <span className="flex items-center">
            <MessageCircle className="h-3 w-3 mr-0.5 text-primary" />
            {commentCount}
          </span>
          <span className="flex items-center">
            <Users className="h-3 w-3 mr-0.5 text-green-600" />
            {participateCount}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
